/* The /dwg-viewer page.
 *
 * Reading happens in a worker (cad-worker.js: acad-ts, a TypeScript port of
 * ACadSharp, MIT), drawing on a canvas (cad-view.js) from one display list
 * (cad-scene.js) that the SVG and PDF exports reuse (cad-export.js), and the
 * DXF export is written by the reader's own writer from the same parsed
 * document. The page only arranges these and talks to the person.
 *
 * Why the rebuild (2026-10-16): the previous in-page reader, written from the
 * ODA specification and checked on 48 tiny synthetic files, opened real
 * drawings badly — AutoCAD 2007 files refused outright (12 % of uploads),
 * 8 of 2 316 objects drawn on one guest's drawing ("шлак"), Cyrillic in
 * 2000/2004 drawings as mojibake, 25–106 s main-thread freezes. Measurements
 * and the seven reader/writer bugs fixed on the way: docs/dwg-viewer.md.
 */
import { toSvg, toPdf } from './cad-export.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__DWG_I18N || {}
const URLS = window.__DWG_URLS || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) s = s.split(':' + n).join(String(v))
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { name: '', size: 0, view: null, data: null, summary: null, limit: null, busy: false }

function setStatus(msg, kind = 'info') {
  const box = $('#dwg-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'dwg-status lg-card dwg-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

/* ---------- worker ---------- */

let worker = null
let seq = 0
const pending = new Map()

function getWorker() {
  if (worker) return worker
  worker = new Worker(URLS.worker)
  worker.onmessage = ev => {
    const m = ev.data || {}
    if (m.type === 'ready') return
    const p = pending.get(m.id)
    if (p) { pending.delete(m.id); p.resolve(m) }
  }
  worker.onerror = ev => {
    /* A reader that blew up takes only its worker; the next file gets a fresh one. */
    for (const p of pending.values()) p.resolve({ ok: false, code: 'worker-crashed', message: String(ev && ev.message || '') })
    pending.clear()
    try { worker.terminate() } catch (_) {}
    worker = null
  }
  return worker
}

function ask(msg, transfer) {
  const id = ++seq
  return new Promise(resolve => {
    pending.set(id, { resolve })
    getWorker().postMessage(Object.assign({ id }, msg), transfer || [])
  })
}

/* ---------- opening ---------- */

async function open(file) {
  state.name = file.name
  state.size = file.size
  $('#dwg-workspace').hidden = true
  $('#dwg-summary').hidden = true
  const started = Date.now()
  setStatus(t('opening', 'Reading the drawing…'))
  /* Big drawings take seconds; a ticking counter says the page is alive. */
  const tick = setInterval(() => {
    const s = Math.round((Date.now() - started) / 1000)
    if (s >= 2) setStatus(t('opening_long', 'Reading the drawing… :s s', { s }))
  }, 1000)
  let bytes
  try {
    bytes = await file.arrayBuffer()
  } catch (err) {
    clearInterval(tick)
    throw err
  }
  const res = await ask({ type: 'open', bytes, name: file.name }, [bytes])
  clearInterval(tick)
  return res
}

function report(res, file) {
  try {
    const s = res.summary || {}
    const m = res.data ? res.data.model : null
    reportShape({
      format: s.kind || (res.sniff && res.sniff.kind) || 'dwg',
      version: s.year || s.release || (res.sniff && res.sniff.release) || null,
      size: file.size,
      ok: !!res.ok && !!m && (m.paths.length > 0 || m.texts.length > 0 || (res.data.layouts || []).length > 0),
      reason: res.ok ? null : res.code,
      flags: [].concat(
        res.ok ? [] : [res.code, res.sniff && res.sniff.kind ? 'sniff-' + res.sniff.kind : null, res.sniff && res.sniff.sig ? 'sig-' + res.sniff.sig : null].filter(Boolean),
        m && m.truncated ? ['truncated'] : [],
        m ? Object.keys(m.skipped || {}).slice(0, 6).map(k => 'skip-' + k) : [],
      ),
      counts: m ? Object.assign({
        paths: m.paths.length / 3, texts: m.texts.length, layouts: res.data.layouts.length,
        readMs: s.readMs, sceneMs: s.sceneMs, notesErr: s.notes ? s.notes.error : 0,
      }, m.counts) : {},
    })
  } catch (_) { /* telemetry never breaks the tool */ }
}

/* KOMPAS-3D (ASCON) documents: drawing, fragment, part, assembly,
   specification, template. Since v17 they are a ZIP around a closed stream, so
   the sniff says «zip» and the old answer was «unpack it and pick the .dwg
   inside» — there is no .dwg inside. 5 sessions in 21 days brought a .cdw here
   (2026-10); the honest route is Save As in KOMPAS itself. */
const KOMPAS_EXT = /\.(cdw|frw|m3d|a3d|spw|kdw)$/i

function refuse(res, file) {
  const code = res.code
  const sn = res.sniff || {}
  const kompas = KOMPAS_EXT.exec((file && file.name) || '')
  let msg
  if (code === 'not-cad' && kompas) {
    msg = t('err_kompas', 'This is a KOMPAS-3D document, not an AutoCAD drawing.', { ext: kompas[1].toLowerCase() })
  } else if (code === 'not-cad') {
    msg = sn.kind === 'zip' ? t('err_zip', 'This is a ZIP archive, not a drawing. Unpack it and pick the .dwg inside.')
      : sn.kind === 'pdf' ? t('err_pdf', 'This is a PDF, not a drawing.')
      : sn.kind === 'xml' ? t('err_lock', 'This looks like an AutoCAD lock or settings file (.dwl, .dwl2), not the drawing. The drawing is the .dwg file next to it.')
      : sn.kind === 'dwg-ancient' ? t('err_old', 'This drawing was written by AutoCAD R12 or earlier, a generation this tool does not read.')
      : t('err_not_dwg', 'This is not an AutoCAD drawing.')
  } else if (code === 'too-old') {
    msg = t('err_old', 'This drawing was written by AutoCAD R12 or earlier, a generation this tool does not read.')
  } else if (code === 'worker-crashed') {
    msg = t('err_memory', 'The drawing is too large for the memory this browser tab has. Close other tabs and try again, or open it on a computer.')
  } else {
    msg = t('err_open', 'This file could not be read: ') + (res.message || code || '')
  }
  setStatus(msg, 'error')
  try { window.__stage && window.__stage('error') } catch (_) {}
  /* not for KOMPAS: by its bytes it is a ZIP, and «open it in the archive tool»
     is the same false lead the old message gave */
  if (code === 'not-cad' && !kompas) suggestOtherTool(file, 'dwg-viewer', $('#dwg-status'), { text: t('suggest_text'), link: t('suggest_link') })
  state.limit && state.limit.abort && state.limit.abort()
}

async function onFile(file) {
  if (!file || state.busy) return
  state.busy = true
  try { window.__stage && window.__stage('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
  /* The unit is the drawing: opening the same file again today is free, and
     every export of it is part of the same unit. */
  state.limit = window.__toolLimit ? window.__toolLimit.begin('dwg-viewer', file.name + ':' + file.size) : null
  if (window.__toolLimit && !state.limit) { state.busy = false; return }
  try {
    const res = await open(file)
    report(res, file)
    if (!res.ok) { refuse(res, file); return }
    show(res)
  } catch (err) {
    setStatus(t('err_open', 'This file could not be read: ') + (err && err.message || err), 'error')
    state.limit && state.limit.abort && state.limit.abort()
    window.__handleError && window.__handleError(err, 'dwg_viewer_open', { say: t('err_open', 'This file could not be read: ') })
  } finally {
    state.busy = false
  }
}

function hasContent(data) {
  const m = data.model
  return m.paths.length > 0 || m.texts.length > 0 || data.layouts.some(l => l.scene.paths.length || l.viewports.length)
}

function show(res) {
  state.data = res.data
  state.summary = res.summary
  const empty = !hasContent(res.data)
  renderSummary(res)
  if (empty) {
    setStatus(t('warn_nothing_to_draw', 'The drawing opened, but holds nothing this tool draws.'), 'warn')
    try { window.__stage && window.__stage('complete') } catch (_) {}
    state.limit && state.limit.abort && state.limit.abort()
    return
  }
  setStatus('')
  $('#dwg-workspace').hidden = false
  if (!state.view) state.view = new window.CadView($('#dwg-canvas'), { dark: readTheme() })
  state.view.setData(res.data)
  renderSpaces()
  renderLayers()
  syncThemeButton()
  try { window.__stage && window.__stage('complete') } catch (_) {}
  state.limit && state.limit.commit && state.limit.commit()
  window.__aeToolComplete && window.__aeToolComplete({
    version: res.summary.year || res.summary.release, kind: res.summary.kind,
    paths: res.data.model.paths.length / 3, layouts: res.data.layouts.length, readMs: res.summary.readMs,
  })
}

/* ---------- summary ---------- */

const SKIP_NOTE = {
  '3DSOLID': 'skip_3d', REGION: 'skip_3d', BODY: 'skip_3d', SURFACE: 'skip_3d', PLANESURFACE: 'skip_3d',
  EXTRUDEDSURFACE: 'skip_3d', REVOLVEDSURFACE: 'skip_3d', SWEPTSURFACE: 'skip_3d', LOFTEDSURFACE: 'skip_3d', NURBSURFACE: 'skip_3d',
  IMAGE: 'skip_image', PDFUNDERLAY: 'skip_underlay', DWFUNDERLAY: 'skip_underlay', DGNUNDERLAY: 'skip_underlay',
  OLE2FRAME: 'skip_ole', OLEFRAME: 'skip_ole',
}

function renderSummary(res) {
  const box = $('#dwg-summary')
  const s = res.summary
  const m = res.data.model
  box.innerHTML = ''
  box.hidden = false
  const chips = el('div', 'dwg-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'dwg-chip' + (kind ? ' dwg-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_version', 'AutoCAD'), s.kind === 'dxf' ? 'DXF ' + (s.year || s.release || '') : (s.year || s.release || '—'))
  chip(t('chip_size', 'Size'), fmtBytes(state.size))
  const total = Object.values(m.counts).reduce((a, b) => a + b, 0)
  chip(t('chip_entities', 'Objects'), total.toLocaleString(window.__intlLocale ? window.__intlLocale() : undefined))
  chip(t('chip_layers', 'Layers'), m.layers.length)
  if (s.blocks) chip(t('chip_blocks', 'Blocks'), s.blocks)
  if (res.data.layouts.length) chip(t('chip_layouts', 'Sheets'), res.data.layouts.length)
  box.append(chips)

  const notes = el('ul', 'dwg-notes')
  const note = (txt, key) => notes.append(el('li', key ? 'dwg-note--key' : null, txt))
  /* Named one by one rather than as a bare "N skipped": "3D solids are not
     drawn" is a precise limit, "12 objects skipped" reads as a failure. */
  const grouped = {}
  for (const [k, n] of Object.entries(m.skipped || {})) {
    const key = SKIP_NOTE[k] || 'skip_other'
    grouped[key] = (grouped[key] || 0) + n
  }
  for (const [key, n] of Object.entries(grouped)) note(t(key, ':n objects are not drawn.', { n }), true)
  if (m.truncated) note(t('note_truncated', 'The drawing is very large; the deepest nested blocks were left out so the page stays responsive.'), true)
  note(t('note_read', 'Read in :ms ms.', { ms: (s.readMs + s.sceneMs).toLocaleString() }))
  box.append(el('h4', 'dwg-sub', t('notes_title', 'What the file says about itself')), notes)
}

/* ---------- toolbar ---------- */

function renderSpaces() {
  const host = $('#dwg-spaces')
  host.innerHTML = ''
  const layouts = state.data.layouts
  host.hidden = !layouts.length
  if (!layouts.length) return
  const mk = (label, i) => {
    const b = el('button', 'dwg-seg', label)
    b.type = 'button'
    b.setAttribute('aria-pressed', String(state.view.space === i))
    b.addEventListener('click', () => {
      state.view.setSpace(i)
      for (const x of host.children) x.setAttribute('aria-pressed', 'false')
      b.setAttribute('aria-pressed', 'true')
      try { window.__aeFunnel && window.__aeFunnel('space', { layout: i >= 0 }) } catch (_) {}
    })
    host.append(b)
  }
  if (state.data.model.paths.length || state.data.model.texts.length) mk(t('space_model', 'Model'), -1)
  layouts.forEach((l, i) => mk(l.name, i))
}

function renderLayers() {
  const box = $('#dwg-layers')
  const list = $('#dwg-layer-list')
  list.innerHTML = ''
  const layers = state.data.model.layers.slice().sort((a, b) => a.name.localeCompare(b.name))
  box.hidden = layers.length < 2
  $('#dwg-layers-count').textContent = String(layers.length)
  for (const L of layers) {
    const label = el('label', 'dwg-layer')
    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.checked = !state.view.hidden.has(L.name)
    cb.addEventListener('change', () => state.view.setLayerVisible(L.name, cb.checked))
    const sw = el('span', 'dwg-swatch')
    sw.style.background = window.CadView.paint(L.rgb, readTheme())
    label.append(cb, sw, document.createTextNode(L.name))
    list.append(label)
  }
}

/* The canvas follows the site theme by default (dark = AutoCAD's model space),
   and the person can flip it; remembered per tool. */
function readTheme() {
  try {
    const saved = localStorage.getItem('dwg-viewer:bg')
    if (saved === 'dark') return true
    if (saved === 'light') return false
  } catch (_) {}
  return document.documentElement.getAttribute('data-theme') !== 'light'
}
function syncThemeButton() {
  const b = $('#dwg-bg')
  if (b && state.view) b.setAttribute('aria-pressed', String(!state.view.dark))
}

/* ---------- exports ---------- */

const stem = () => state.name.replace(/\.(dwg|dxf|bak|sv\$)$/i, '') || 'drawing'
const spaceSuffix = () => (state.view && state.view.space >= 0 && state.data.layouts[state.view.space]) ? '-' + state.data.layouts[state.view.space].name : ''
const safeName = s => (window.__outputName && window.__outputName.sanitize) ? window.__outputName.sanitize(s) : s.replace(/[\\/:*?"<>|]+/g, '_')

async function exportDxf(btn) {
  if (!state.data) return
  btn.disabled = true
  try {
    const r = await ask({ type: 'export', fmt: 'dxf' })
    if (!r.ok) throw new Error(r.message || r.code)
    await window.__downloadFile(new Blob([r.bytes], { type: 'application/dxf' }), safeName(stem() + '.dxf'))
  } catch (err) {
    window.__handleError && window.__handleError(err, 'dwg_viewer_dxf', { say: t('err_export', 'Could not write the file.') })
  } finally { btn.disabled = false }
}

async function exportSvg(btn) {
  if (!state.view) return
  btn.disabled = true
  try {
    const svg = toSvg(state.view)
    if (!svg) return
    await window.__downloadFile(new Blob([svg], { type: 'image/svg+xml' }), safeName(stem() + spaceSuffix() + '.svg'))
  } catch (err) {
    window.__handleError && window.__handleError(err, 'dwg_viewer_svg', { say: t('err_export', 'Could not write the file.') })
  } finally { btn.disabled = false }
}

async function exportPdf(btn) {
  if (!state.view) return
  btn.disabled = true
  const label = btn.textContent
  btn.textContent = t('export_pdf_busy', 'Preparing PDF…')
  try {
    const bytes = await toPdf(state.view, { pdfLib: URLS.pdfLib, textFont: URLS.textFont, wm: URLS.wm, title: stem() + spaceSuffix() })
    if (!bytes) return
    await window.__downloadFile(new Blob([bytes], { type: 'application/pdf' }), safeName(stem() + spaceSuffix() + '.pdf'))
  } catch (err) {
    window.__handleError && window.__handleError(err, 'dwg_viewer_pdf', { say: t('err_export', 'Could not write the file.') })
  } finally { btn.disabled = false; btn.textContent = label }
}

/* ---------- arrival with the drawing already in hand ----------
   /file-converter does not convert drawings; its row for a DWG links here and
   puts the file under HANDOFF_KEY first (window.__toolHandoffPut). Before
   2026-10-28 the link came empty-handed, after the person had already picked
   the file once (requests #2092 «DWG → PDF», #2138 «DWG → cdw»). The drawing
   goes through the zone exactly as if it were dropped, so the cap, the picked-
   file row and the telemetry behave as usual. Single use: the take deletes the
   carry; a stale one (30 min) comes back null. */
const HANDOFF_KEY = 'fc:dwg'

function takeHandoff(zone) {
  if (!zone || typeof window.__toolHandoffTake !== 'function') return
  window.__toolHandoffTake(HANDOFF_KEY).then(file => {
    if (!file || state.busy || state.data) return
    let tries = 0
    /* the zone wires itself on its own DOMContentLoaded — give it a moment */
    const put = () => {
      if (state.busy || state.data) return
      if (typeof zone.__uploaderInjectFile === 'function') {
        zone.__uploaderInjectFile(file, 'handoff')
        try { window.__aeFeature && window.__aeFeature('handoff_in', { from: 'file-converter', mb: Math.round(file.size / 1048576) }) } catch (_) {}
        return
      }
      if (++tries < 40) setTimeout(put, 100)
    }
    put()
  }, () => { /* no carry — the page simply waits for a file */ })
}

/* ---------- wiring ---------- */

function wire() {
  const zone = document.getElementById('dwgDrop')
  const onPick = ev => {
    const d = ev && ev.detail
    const file = d && d.files && d.files.length ? d.files[0] : d && d.file
    if (file) onFile(file)
  }
  /* The zone hands a pick made before this module loaded to the late listener
     itself (layout stub + tool-uploader.js). */
  if (window.__bindUploaderFile) window.__bindUploaderFile(zone, file => onFile(file))
  else if (zone) zone.addEventListener('tool-uploader:file', onPick)
  zone && zone.addEventListener('tool-uploader:clear', () => {
    state.data = null
    $('#dwg-workspace').hidden = true
    $('#dwg-summary').hidden = true
    setStatus('')
  })
  $('#dwg-fit').addEventListener('click', () => state.view && state.view.fit())
  $('#dwg-zoom-in').addEventListener('click', () => state.view && state.view.zoom(1.5))
  $('#dwg-zoom-out').addEventListener('click', () => state.view && state.view.zoom(1 / 1.5))
  $('#dwg-bg').addEventListener('click', () => {
    if (!state.view) return
    state.view.setDark(!state.view.dark)
    try { localStorage.setItem('dwg-viewer:bg', state.view.dark ? 'dark' : 'light') } catch (_) {}
    syncThemeButton()
    renderLayers()
  })
  $('#dwg-export-pdf').addEventListener('click', e => exportPdf(e.currentTarget))
  $('#dwg-export-dxf').addEventListener('click', e => exportDxf(e.currentTarget))
  $('#dwg-export-svg').addEventListener('click', e => exportSvg(e.currentTarget))
  takeHandoff(zone)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
